import { bigIntMax, bigIntMin, DEFAULT_DECIMALS, scaleUp } from "logic/utils";

import { type LoanDetailsState } from "../LoanDetails/LoanDetailsProvider";

export function resolveRepaymentPlan(
  balances: LoanDetailsState["balances"],
  paymentAmount: bigint | undefined,
  pyusdDecimals: number,
  usdaiLocked: bigint,
  reservesRequired: bigint | undefined,
): RepaymentPlan {
  if (balances == null || paymentAmount == undefined || reservesRequired == undefined) {
    return {
      status: "LOADING",
      legs: [],
      repayAmount: 0n,
      deficit: 0n,
      amounts: { transferAmount: 0n, depositAmount: 0n, bridgeAmount: 0n },
    };
  }

  // `required` is reserved for upcoming payments, so it can't fund this one —
  // counting it would understate the deficit and make repay() revert.
  const raUsdai = bigIntMax(balances.reserve.usdaiMain - reservesRequired, 0n);
  const availableUsdai = bigIntMax(balances.eoa.usdaiMain - usdaiLocked, 0n);
  const pyusdMain = balances.eoa.pyusdMain;
  const pyusdPeer = balances.eoa.pyusdPeer;

  const deficit = bigIntMax(paymentAmount - raUsdai, 0n);

  const legs: RepaymentLeg[] = [];
  let remaining = deficit;

  /* 1. USDai in EOA — transfer to RA */
  if (remaining > 0n && availableUsdai > 0n) {
    const take = bigIntMin(remaining, availableUsdai);
    legs.push({ type: "TRANSFER_USDAI", amount: take });
    remaining -= take;
  }

  /* 2. PYUSD on main chain — deposits 1:1 as USDai via localCompose */
  if (remaining > 0n && pyusdMain > 0n) {
    const take = bigIntMin(ceilToPyusd(remaining, pyusdDecimals), pyusdMain);
    legs.push({ type: "DEPOSIT_PYUSD", amount: take });
    remaining = bigIntMax(remaining - scaleUp(take, pyusdDecimals), 0n);
  }

  /* 3. PYUSD on peer chain — bridged + deposited, over-provisioned to absorb
     interest accrual while the bridge is in flight */
  if (remaining > 0n && pyusdPeer > 0n) {
    const needed = ceilToPyusd(remaining, pyusdDecimals);
    const buffered = bigIntMin(needed + (needed * BRIDGE_BUFFER_BPS) / 10_000n + 1n, pyusdPeer);
    legs.push({ type: "BRIDGE_PYUSD", amount: buffered });
    // insufficiency is judged on the unbuffered need
    remaining = bigIntMax(remaining - scaleUp(bigIntMin(needed, pyusdPeer), pyusdDecimals), 0n);
  }

  const amounts = legs.reduce(
    (acc, leg) => {
      if (leg.type === "TRANSFER_USDAI") {
        acc.transferAmount = leg.amount;
      } else if (leg.type === "DEPOSIT_PYUSD") {
        acc.depositAmount = leg.amount;
      } else if (leg.type === "BRIDGE_PYUSD") {
        acc.bridgeAmount = leg.amount;
      }
      return acc;
    },
    { transferAmount: 0n, depositAmount: 0n, bridgeAmount: 0n },
  );

  return {
    status: remaining > 0n ? "INSUFFICIENT_BALANCE" : "READY",
    legs,
    repayAmount: paymentAmount,
    deficit,
    amounts,
  };
}

/**
 * A funding source the repayment taps, in priority order. Amounts are in the
 * source token's native decimals (USDai 18, PYUSD 6).
 *
 * - TRANSFER_USDAI: EOA USDai -> RA, main chain (batchable)
 * - DEPOSIT_PYUSD:  approve? + OUSDaiUtility.localCompose to RA, main chain (batchable)
 * - BRIDGE_PYUSD:   approve? + OAdapter.send, peer chain (must be its own tx)
 */
export type RepaymentLeg =
  | { type: "TRANSFER_USDAI"; amount: bigint }
  | { type: "DEPOSIT_PYUSD"; amount: bigint }
  | { type: "BRIDGE_PYUSD"; amount: bigint };

export interface RepaymentPlan {
  status: "LOADING" | "READY" | "INSUFFICIENT_BALANCE";
  /** Legs in priority/execution order; empty when RA already covers the payment */
  legs: RepaymentLeg[];
  /** The repay() amount, always the last call of the main-chain batch */
  repayAmount: bigint;
  /** Shortfall vs the RA balance, 18 decimals */
  deficit: bigint;
  /** The amounts for each leg */
  amounts: { transferAmount: bigint; depositAmount: bigint; bridgeAmount: bigint };
}

/** Over-provision applied to the bridge leg (0.1%), capped at the peer balance */
const BRIDGE_BUFFER_BPS = 10n;

// scaleDown(n, d, "up") adds a full unit even on exact multiples, which could
// round a leg above the source balance — use a true ceiling division instead.
function ceilToPyusd(amount18: bigint, pyusdDecimals: number): bigint {
  const unit = 10n ** BigInt(DEFAULT_DECIMALS - pyusdDecimals);
  return (amount18 + unit - 1n) / unit;
}
